import * as React from 'react';

export interface IUseIntersectionVisibleOnceOptions {
  /** Если false — observer не вешается (по умолчанию true) */
  enabled?: boolean;
  threshold?: number | number[];
  root?: Element | Document | null;
  rootMargin?: string;
}

/**
 * Вызывает onVisible один раз, когда наблюдаемый элемент пересекает viewport (IntersectionObserver).
 * Возвращает ref-callback для привязки к DOM-элементу.
 */
export function useIntersectionVisibleOnce(
  onVisible: () => void,
  options?: IUseIntersectionVisibleOnceOptions,
): React.RefCallback<Element> {
  const { enabled = true, threshold = 0.01, root = null, rootMargin } = options ?? {};
  const [node, setNode] = React.useState<Element | null>(null);
  const onVisibleRef = React.useRef(onVisible);

  React.useLayoutEffect(() => {
    onVisibleRef.current = onVisible;
  }, [onVisible]);

  React.useLayoutEffect(() => {
    if (!enabled) {
      setNode(null);
    }
  }, [enabled]);

  React.useLayoutEffect(() => {
    if (!enabled || !node) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          onVisibleRef.current();
          observer.disconnect();
        }
      },
      {
        threshold,
        root: root ?? undefined,
        rootMargin,
      },
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, [enabled, node, threshold, root, rootMargin]);

  return React.useCallback(
    (el: Element | null) => {
      if (!enabled) {
        setNode(null);

        return;
      }
      setNode(el);
    },
    [enabled],
  );
}
